import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Box } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { useState } from "react";

import { ExperimentCard } from "@/Components/v2/atoms/Card/ExperimentCard";
import { BetaPill } from "@/Components/v2/atoms/Pill";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { WordByWordLoader } from "@/Components/v2/atoms/WordByWordLoader/WordByWordLoader";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import { TransformedExperiment } from "../api/getTestingRecommendations";
import { ExperimentDetails } from "./ExperimentDetails";

interface PowerPlaysProps {
  experiments: TransformedExperiment[];
  selectedExperiment: string | null;
  isLoading: boolean;
  error: any;
  onExperimentClick: (experiment: TransformedExperiment) => void;
  rawData?: any; // Add raw data prop for debug view
}

export const PowerPlays = ({
  experiments,
  selectedExperiment,
  isLoading,
  error,
  onExperimentClick,
  rawData,
}: PowerPlaysProps) => {
  const { value: isDebugEnabled } = useFeatureGate(featureFlag.growthosDebugFlag);
  const [isDebugExpanded, setIsDebugExpanded] = useState(false);
  const selectedExperimentData = experiments.find((exp) => exp.id === selectedExperiment);

  return (
    <Box>
      <Box display={"flex"} alignItems={"center"}>
        <Typography variant="h5" fontWeight={600}>
          Power Plays
        </Typography>
        <BetaPill />
      </Box>
      <Typography variant="body2" mb={2}>
        MAITE has spotted high-impact experiments based on the last 6 months of performance.
        Recommendations auto-update after each data refresh. Add to your plan or test now in
        Scenario Builder.
      </Typography>

      {isLoading ? (
        <Box sx={{ textAlign: "center", pb: 3 }}>
          <WordByWordLoader
            words={["Generating", "Power Plays..."]}
            interval={700}
            typographyProps={{ fontWeight: 600 }}
          />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="error">
            Failed to load testing recommendations. Please try again.
          </Typography>
        </Box>
      ) : experiments.length > 0 ? (
        <Stack direction={"row"}>
          <Stack spacing={2} width={"35%"} direction={"column"}>
            {experiments.map((experiment) => (
              <ExperimentCard
                key={experiment.id}
                title={experiment.title}
                description={experiment.description}
                tags={experiment.tags}
                isSelected={selectedExperiment === experiment.id}
                onClick={() => onExperimentClick(experiment)}
              />
            ))}
          </Stack>
          <Box width={"65%"} pl={3}>
            {selectedExperimentData ? (
              <ExperimentDetails experiment={selectedExperimentData} />
            ) : (
              <Box
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Select an experiment to view details
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      ) : (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No testing recommendations available at this time.
          </Typography>
        </Box>
      )}

      {/* Debug Accordion - Only shown when feature flag is enabled */}
      {isDebugEnabled && rawData && (
        <Box sx={{ mt: 3 }}>
          <Accordion
            expanded={isDebugExpanded}
            onChange={() => setIsDebugExpanded(!isDebugExpanded)}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              "&:before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Debug Data - Raw JSON Response
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                maxHeight: "400px",
                overflow: "auto",
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: "12px",
                  lineHeight: "1.4",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {JSON.stringify(
                  {
                    startDate: rawData.startDate,
                    endDate: rawData.endDate,
                    marketingData: rawData.marketingData,
                  },
                  null,
                  2
                )}
              </pre>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};
